import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../App';

// Party colour map
const PARTY_COLORS = {
  DMK:    '#E31E24',
  AIADMK: '#006400',
  BJP:    '#FF9933',
  INC:    '#138808',
  IND:    '#6B7280',
};

function partyColor(short) { return PARTY_COLORS[short] || '#1B3A6B'; }

function formatTs(unixSec) {
  return new Date(unixSec * 1000).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
}

// ── Bar Chart ─────────────────────────────────────────────────────────────────
function BarChart({ results, candidates, total }) {
  const maxVotes = Math.max(...Object.values(results).map(v => v.count), 1);

  return (
    <div className="bar-chart">
      {candidates.map(cand => {
        const info  = results[cand.name] || { count: 0, party_short: cand.party_short };
        const count = info.count;
        const barPct  = (count / maxVotes) * 100;
        const sharePct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
        const color   = partyColor(cand.party_short);

        return (
          <div key={cand.name} className="bar-row">
            <div className="bar-label">
              {cand.name}
              <span className="bar-party-tag">{cand.party_short}</span>
            </div>
            <div className="bar-track">
              <div
                className="bar-fill"
                style={{ width: `${barPct}%`, background: color }}
              />
            </div>
            <span className="bar-value">
              {count}&nbsp;
              <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({sharePct}%)</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Tampered Alerts Panel ─────────────────────────────────────────────────────
function TamperedPanel({ items }) {
  if (items.length === 0) {
    return (
      <div className="tampered-section">
        <div className="tampered-header">
          <span className="tampered-header-icon">🔒</span>
          <span className="tampered-header-title">
            மோசடி கண்காணிப்பு | Tamper Detection Monitor
          </span>
          <span className="tampered-header-count">0 Attempts</span>
        </div>
        <div className="tampered-none">
          ✅ தேர்தல் மோசடி இல்லை — No tampered vote attempts detected
        </div>
      </div>
    );
  }

  return (
    <div className="tampered-section">
      <div className="tampered-header">
        <span className="tampered-header-icon">🚨</span>
        <span className="tampered-header-title">
          மோசடி வாக்குகள் | TAMPERED VOTE ATTEMPTS DETECTED
        </span>
        <span className="tampered-header-count">{items.length} Attempt{items.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="tampered-body">
        {items.map(item => (
          <div key={item.id} className="tampered-item">
            <div className="tampered-item-icon">🚫</div>
            <div className="tampered-item-detail">
              <span className="tampered-voter-id">
                Fingerprint Slot #{item.voter_id}
              </span>
              <span className="tampered-candidate">
                &nbsp;tried to vote for <strong>{item.attempted_candidate}</strong>
                &nbsp;— already voted for <strong>{item.original_candidate}</strong>
              </span>
              <div className="tampered-time">
                🕐 {formatTs(item.timestamp)} &nbsp;|&nbsp; ⛓️ Blockchain tamper log entry #{item.id}
              </div>
            </div>
            <span className="badge badge-danger">TAMPERED</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function ResultsDashboard() {
  const navigate = useNavigate();
  const [results,    setResults]    = useState({});
  const [candidates, setCandidates] = useState([]);
  const [total,      setTotal]      = useState(0);
  const [tampered,   setTampered]   = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error,      setError]      = useState('');

  const fetchAll = useCallback(() => {
    // Fetch results and tampered in parallel
    Promise.all([
      fetch(`${API_BASE}/api/results`).then(r => r.json()),
      fetch(`${API_BASE}/api/tampered`).then(r => r.json()),
    ])
      .then(([rData, tData]) => {
        setResults(rData.results    || {});
        setCandidates(rData.candidates || []);
        setTotal(rData.total         || 0);
        setTampered(tData.tampered   || []);
        setLastUpdate(new Date().toLocaleTimeString('en-IN'));
        setError('');
      })
      .catch(() => setError('Could not reach the server. Check Flask is running.'));
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 3000);
    return () => clearInterval(id);
  }, [fetchAll]);

  return (
    <div className="screen">
      {/* ── Header ──────────────────────────────────────── */}
      <div className="results-header-row">
        <div>
          <button className="back-btn" onClick={() => navigate('/')}>← முகப்பு | Home</button>
          <div className="section-title" style={{ marginTop: 10 }}>
            நேரடி முடிவுகள் | Live Election Results
          </div>
          <p className="section-subtitle" style={{ marginBottom: 0 }}>
            2026 Tamil Nadu Legislative Assembly Election &nbsp;·&nbsp;
            {lastUpdate ? `கடைசி புதுப்பிப்பு Last updated ${lastUpdate}` : 'பதிவேற்றுகிறது…'}
          </p>
        </div>
        <div className="live-indicator">
          <span className="live-dot" />
          நேரடி | Live — refreshing every 3s
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>⚠️</span><span>{error}</span>
        </div>
      )}

      {/* ── Stats row ───────────────────────────────────── */}
      <div className="results-stats-row">
        <div className="stat-card">
          <div className="stat-number">{total}</div>
          <div className="stat-label">மொத்த வாக்குகள் | Total Votes</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{candidates.length}</div>
          <div className="stat-label">வேட்பாளர்கள் | Candidates</div>
        </div>
        <div className={`stat-card${tampered.length > 0 ? ' danger-stat' : ''}`}>
          <div className="stat-number">{tampered.length}</div>
          <div className="stat-label">
            {tampered.length > 0 ? '🚨 மோசடி | TAMPERED' : 'மோசடி இல்லை | No Tampers'}
          </div>
        </div>
      </div>

      {/* ── Bar chart ───────────────────────────────────── */}
      <div className="results-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ color: 'var(--tnec-navy)', fontWeight: 800 }}>
            வேட்பாளர் முடிவுகள் | Candidate Results
          </h3>
          <button className="btn btn-secondary btn-sm" onClick={fetchAll}>↻ Refresh</button>
        </div>

        {candidates.length > 0
          ? <BarChart results={results} candidates={candidates} total={total} />
          : <p className="text-muted" style={{ textAlign: 'center', padding: '24px 0' }}>
              வாக்குகள் பதிவாகவில்லை — No votes recorded yet.
            </p>
        }
      </div>

      {/* ── Tampered alert panel ─────────────────────────── */}
      <div style={{ marginBottom: 4 }}>
        <div className="section-title">
          மோசடி கண்காணிப்பு | Tamper Detection
        </div>
        <p className="section-subtitle">Real-time blockchain tamper alert log</p>
      </div>
      <TamperedPanel items={tampered} />

    </div>
  );
}
